import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as YaAuth } from './static/yandex.svg';
import { ReactComponent as VkOneTap } from './static/VKOneTap.svg';
import './AuthVariants.css';
import request from '../../../app/api/requests';
import { clearVkAttempt, hasVkAttempt, saveVkAttempt, vkAttemptLifetime } from './vk-auth-flow';

interface AuthVariantsProps {
    onClickFunction?: () => void;
    isPrivacyPolicy: boolean;
}

export default function AuthVariants({ onClickFunction, isPrivacyPolicy = false }: AuthVariantsProps) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const pending = useRef<{ popup: Window; controller: AbortController; state?: string; timer?: number }>();

    const disposeAttempt = () => {
        const attempt = pending.current;
        pending.current = undefined;
        if (!attempt) return;
        window.clearInterval(attempt.timer);
        attempt.controller.abort();
        if (attempt.state) clearVkAttempt(attempt.state);
        if (!attempt.popup.closed) attempt.popup.close();
    };

    useEffect(() => () => disposeAttempt(), []);

    const handleClick = async (provider: 'vk' | 'yandex') => {
        onClickFunction?.();
        if (!isPrivacyPolicy || pending.current) return;
        setError('');
        if (typeof BroadcastChannel === 'undefined') {
            setError('Этот браузер не поддерживает вход в отдельном окне. Используйте другой браузер.');
            return;
        }
        // Open synchronously within the click; browsers can block windows opened after an HTTP response.
        const popup = window.open('about:blank', '_blank', 'popup,width=600,height=760');
        if (!popup) {
            setError('Разрешите всплывающие окна для входа и повторите попытку.');
            return;
        }
        const attempt = { popup, controller: new AbortController(), state: undefined as string | undefined, timer: undefined as number | undefined };
        pending.current = attempt;
        setBusy(true);
        try {
            const response = await request.get(`/auth/${provider}/login`, { signal: attempt.controller.signal, timeout: 20000 });
            if (pending.current !== attempt) return;
            const url = provider === 'vk' ? response.data.authUrl : response.data;
            if (typeof url !== 'string' || !url.startsWith('https://')) throw new Error('Invalid authorization URL');
            if (provider === 'vk') {
                saveVkAttempt(response.data.state, response.data.codeVerifier);
                attempt.state = response.data.state;
            }
            popup.location.href = url;
            const expiresAt = Date.now() + vkAttemptLifetime;
            attempt.timer = window.setInterval(() => {
                const consumed = attempt.state && !hasVkAttempt(attempt.state);
                if (consumed || popup.closed || Date.now() >= expiresAt) {
                    disposeAttempt();
                    setBusy(false);
                    if (!consumed) setError('Вход не завершён. Нажмите кнопку сервиса, чтобы попробовать снова.');
                }
            }, 500);
        } catch {
            if (pending.current !== attempt) return;
            disposeAttempt();
            setBusy(false);
            setError('Не удалось начать вход. Проверьте соединение и повторите попытку.');
        }
    };

    return (
        <div className="AuthVariants__container">
            <p className="AuthVarinats__descr">или</p>
            <div className="AuthVariants__btn-group">
                <button type="button" id="vkAuth" className="vkAuth" aria-label="Войти через VK" disabled={busy} onClick={() => handleClick('vk')}><VkOneTap /></button>
                <button type="button" id="YaAuth" className="YaAuth" aria-label="Войти через Яндекс" disabled={busy} onClick={() => handleClick('yandex')}><YaAuth /></button>
            </div>
            {busy && <p role="status">Завершите вход в открывшемся окне.</p>}
            {error && <p role="alert">{error}</p>}
        </div>
    );
}
